import {
  Editor,
  Node,
  Range,
  Transforms,
  Path,
  Element as SlateElement
} from 'slate';

import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import HelptapEditor from '../';

export const PREVIEW_ROWS_COUNT = 10;

export function getDefaultOptions(overrides) {

  return basicEditorValueSerializer.utilities.node.dataCollection.getDefaultOptions(overrides);

}

export function buildNode(options) {

  return basicEditorValueSerializer.utilities.node.dataCollection.buildNode(options);

}

export function insert(editor, options) {

  const node = buildNode(options);

  HelptapEditor.insertNode(editor, node);

}

export function initialize(editor, node, nodePath, data) {

  const { properties, children } = basicEditorValueSerializer.utilities.node.dataCollection.initializeNode(node, data);
  properties.connected = data.connected || false;

  if (children.length > PREVIEW_ROWS_COUNT) {
    children.length = PREVIEW_ROWS_COUNT;
  }
  delete properties.rows;
  HelptapEditor.setAttributes(editor, { properties: { ...node.properties, ...properties } }, nodePath);
  HelptapEditor.replaceChildren(editor, nodePath, children, node.children.length);

}

export function reInitialize(editor, node, nodePath, data, filterId) {

  initialize(editor, node, nodePath, data);

  const newNode = HelptapEditor.getNodeByPath(editor, nodePath);

  HelptapEditor.setAttributes(editor, {
    properties: {
      ...newNode.properties,
      filterId
    }
  }, nodePath);

}

export function refreshNode(node, rowNode) {

  return basicEditorValueSerializer.utilities.node.dataCollection.refreshNode(node, rowNode);

}

export function toggle(editor, options, editorOptions) {

  const node = buildNode(options);

  HelptapEditor.setNodeOptions(editor, node, editorOptions);

}

export function cloneNode(node) {

  return node;

}

export function duplicate(editor, node, nodePath) {

  const clonedNode = cloneNode(HelptapEditor.cloneNode(node));

  HelptapEditor.insertNode(editor, clonedNode, nodePath);

}

export function extractStylesFromNode(node) {
  return basicEditorValueSerializer.utilities.dataCollections.extractStylesFromNode(node);
}

export function extractStylesFromTextNode(node) {
  return basicEditorValueSerializer.utilities.dataCollections.extractStylesFromTextNode(node);
}

function extractTemplateStyles(template) {

  const styles = [];

  for (let node of template) {
    styles.push({
      node: extractStylesFromNode(node),
      text: extractStylesFromTextNode(node)
    });
  }

  return styles;

}

function copyRowAttributesToRows(editor, nodePath, rowNode) {

  const rowAttributes = { ...rowNode };
  delete rowAttributes.properties;
  delete rowAttributes.children;

  Editor.withoutNormalizing(editor, () => {

    for (const [, rowNodePath] of Node.children(editor, nodePath)) {
      HelptapEditor.setAttributes(editor, rowAttributes, rowNodePath);
    }

  });

}

export function tryRefreshNode(editor, node, nodePath, rowNode) {

  const template = basicEditorValueSerializer.utilities.dataCollections.buildTemplateFromRowChildren(rowNode.children);

  if (template.length === 0) {
    template.push(basicEditorValueSerializer.utilities.node.paragraph.buildNode());
  }

  if (JSON.stringify(template) === JSON.stringify(node.properties.template)) {
    copyRowAttributesToRows(editor, nodePath, rowNode);
    return false;
  }

  const defaultTextNodeStyles = basicEditorValueSerializer.utilities.dataCollections.getDefaultTextNodeStyles();
  const styles = extractTemplateStyles(template);

  Editor.withoutNormalizing(editor, () => {

    HelptapEditor.setAttributes(editor, { properties: { ...node.properties, template } }, nodePath);

    for (const [, rowNodePath] of Node.children(editor, nodePath)) {

      let index = 0;

      for (const [cellNode, cellNodePath] of Node.children(editor, rowNodePath)) {

        const style = styles[index];

        if (style === void 0) {
          Transforms.removeNodes(editor, { at: cellNodePath });
          continue;
        }

        const textNodePath = [...cellNodePath, 0];
        HelptapEditor.setAttributes(editor, defaultTextNodeStyles, textNodePath);
        HelptapEditor.setAttributes(editor, {
          ...style.node,
          type: template[index].type,
          properties: {
            ...cellNode.properties,
            ...template[index].properties
          }
        }, cellNodePath);
        HelptapEditor.setAttributes(editor, style.text, textNodePath);

        index++;

      }

    }
  });

  return true;

}

export function getDataCollectionFromChild(editor, path) {

  const nodeEntry = Editor.above(editor, {
    at: path,
    match: n => {
      return n.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION;
    }
  });

  return nodeEntry;

}

export function getColumnsByPath(editor, path) {

  const nodeEntry = getDataCollectionFromChild(editor, path);

  if (!nodeEntry) {
    return [];
  }

  const [node] = nodeEntry;

  return node.properties.columns;

}

export function isDataCollectionDescendant(editor, path) {

  const nodeEntry = Editor.above(editor, {
    at: path,
    match: n => {
      return n.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION;
    }
  });

  return !!nodeEntry;

}

export function isDataCollectionDescendantSelected(editor) {

  const nodeEntry = Editor.above(editor, {
    at: editor.selection,
    match: n => {
      return n.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION;
    }
  });

  return !!nodeEntry;

}

export function isDataCollectionNode(node) {

  return node.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION;

}

export function isDataCollectionRowPageNode(node) {

  return node.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION_ROW_PAGE;

}

export function isDataCollectionRowPageNodeDescendant(editor, path) {

  const nodeEntry = Editor.above(editor, {
    at: path,
    match: n => {
      return n.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION_ROW_PAGE;
    }
  });

  return !!nodeEntry;

}

export function isDataCollectionNodeDescendant(editor, path) {

  const nodeEntry = Editor.above(editor, {
    at: path,
    match: n => {
      return n.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION;
    }
  });

  return !!nodeEntry;

}

export function getParentDataCollectionNode(editor, path) {

  const nodeEntry = Editor.above(editor, {
    at: path,
    match: n => {
      return n.type === basicEditorValueSerializer.allTypes.types.DATA_COLLECTION;
    }
  });

  return !!nodeEntry ? nodeEntry : null;

}

export function updateRows(editor, node, nodePath, rowsCount, rows) {

  Editor.withoutNormalizing(editor, () => {

    const properties = node.properties;
    const columns = properties.columns;
    const template = properties.template;

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...node.properties,
        rowsCount
      }
    }, nodePath);

    let children = basicEditorValueSerializer.utilities.node.dataCollection.buildRows(columns, template, rows);

    if (children.length > PREVIEW_ROWS_COUNT) {
      children.length = PREVIEW_ROWS_COUNT;
    }

    HelptapEditor.replaceChildren(editor, nodePath, children, node.children.length);

  });

}

export function updateColumns(editor, node, columns, nodePath, rows) {

  Editor.withoutNormalizing(editor, () => {

    const properties = node.properties;
    const template = basicEditorValueSerializer.utilities.node.dataCollection.refreshTemplate(columns, properties.template);
    let limitedRows = rows;

    if (limitedRows.length > PREVIEW_ROWS_COUNT) {
      limitedRows = limitedRows.slice(0, PREVIEW_ROWS_COUNT);
    }

    const children = basicEditorValueSerializer.utilities.node.dataCollection.buildRows(columns, template, limitedRows);

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...node.properties,
        template,
        columns
      }
    }, nodePath);

    HelptapEditor.replaceChildren(editor, nodePath, children, node.children.length);

  });

}

export function reorder(editor, from, to) {

  // Find the parent of from
  // Make a copy of it
  // Move from, to within the copy

  const fromParent = Editor.parent(editor, from);
  const fromParentCopy = HelptapEditor.cloneNode(fromParent[0]);
  const fromIndex = from.slice(-1)[0];
  const toIndex = to.slice(-1)[0];

  const fromNode = fromParentCopy.children[fromIndex];
  const toNode = fromParentCopy.children[toIndex];

  fromParentCopy.children[fromIndex] = toNode;
  fromParentCopy.children[toIndex] = fromNode;

  const [dataCollectionNode, dataCollectionPath] = getDataCollectionFromChild(editor, from);

  const template = basicEditorValueSerializer.utilities.dataCollections.buildTemplateFromRowChildren(fromParentCopy.children);

  if (JSON.stringify(template) === JSON.stringify(dataCollectionNode.properties.template)) {
    return false;
  }

  const lowIndex = Math.min(fromIndex, toIndex);
  const highIndex = Math.max(fromIndex, toIndex);

  Editor.withoutNormalizing(editor, () => {

    HelptapEditor.setAttributes(editor, { properties: { ...dataCollectionNode.properties, template } }, dataCollectionPath);

    for (const [, rowNodePath] of Node.children(editor, dataCollectionPath)) {
      Transforms.moveNodes(editor, { at: [...rowNodePath, highIndex], to: [...rowNodePath, lowIndex] });
      Transforms.moveNodes(editor, { at: [...rowNodePath, lowIndex + 1], to: [...rowNodePath, highIndex] });
    }

  });

}

export function refreshRowPage(editor, node, path, manager, record) {

  const template = [basicEditorValueSerializer.utilities.node.dataCollection.rebuildDataCollectionRowPageNode(manager)];
  const nodes = basicEditorValueSerializer.utilities.node.dataCollection.generateRowPage({ ...manager, template }, record);
  const newNode = nodes[0];

  HelptapEditor.replaceChildren(editor, path, newNode.children, node.children.length);

}

export function isDatabaseConnectedNode(node) {

  if (!node.properties) {
    return false;
  }
  if (typeof node.properties.columnId !== 'string') {
    return false;
  }

  return true;

}

export function isDatabaseConnectedNodeSelected(editor) {

  const { selection } = editor;

  if (!selection) {
    return false;
  }

  if (Range.isCollapsed(selection)) {
    return false;
  }

  const [node] = Editor.nodes(editor, {
    match: isDatabaseConnectedNode
  });

  if (!node) {
    return false;
  }

  return true;

}

export function getConnectedColumnIds(editor) {

  const columnIds = [];

  function walkNodes(nodes) {

    for (let node of nodes) {

      if (typeof node.text === 'string') {
        continue;
      }

      const properties = node.properties || {};
      const columnId = properties.columnId;

      if (typeof columnId === 'string') {
        columnIds.push(columnId);
        continue;
      }

      walkNodes(node.children);

    }

  }

  walkNodes(editor.children);

  return columnIds;

}

export function insertColumnNode(editor, column, record) {

  const template = basicEditorValueSerializer.utilities.dataCollections.buildDefaultNodeForColumn(column);
  const node = basicEditorValueSerializer.utilities.dataCollections.buildNodeFromTemplate(column, template, record.data);

  HelptapEditor.insertNode(editor, node, [editor.children.length]);

}

export function deleteColumnNode(editor, columnId) {
  // Collect all matching nodes and paths first
  const nodes = Array.from(Editor.nodes(editor, {
    at: [],
    match(node) {
      const properties = node.properties || {};
      return properties.columnId === columnId;
    }
  }));

  // Sort paths in reverse order to avoid invalidating future paths
  const paths = nodes.map(([, path]) => path).sort((a, b) => Path.compare(b, a));

  // Perform deletion without normalizing
  Editor.withoutNormalizing(editor, () => {
    for (const path of paths) {
      Transforms.removeNodes(editor, { at: path });
    }
  });
}

function isLinkNode(editor, node) {

  return editor.isVoid(node) === false && node.properties.connected;
}

export function isConnected(editor) {

  const path = HelptapEditor.getDraggableNodePath(editor);
  if (path === null) {
    return false;
  }

  const node = Node.get(editor, path);

  return isLinkNode(editor, node);

}

export function toggleConnectedPageLink(editor) {

  Editor.withoutNormalizing(editor, () => {

    const path = HelptapEditor.getDraggableNodePath(editor);
    if (path === null) {
      return;
    }
    const node = Node.get(editor, path);

    if (isLinkNode(editor, node) === true) {
      Transforms.setNodes(editor, {
        properties: {
          ...node.properties,
          connected: false
        }
      }, { at: path });
      Transforms.unwrapNodes(editor, {
        match: n => n.type === 'link',
        split: true
      });
    } else {
      const node = Node.get(editor, path);
      Transforms.setNodes(editor, {
        properties: {
          ...node.properties,
          connected: true
        }
      }, {
        at: path
      });

    }

  });

}

function disconnectLink(editor, node, path) {

  Editor.withoutNormalizing(editor, () => {

    const rowPath = Path.parent(path);
    const dataCollectionPath = Path.parent(rowPath);

    const nodeIndex = path[path.length - 1];

    for (const [, rowNodePath] of Node.children(editor, dataCollectionPath)) {

      const cellNodePath = [...rowNodePath, nodeIndex];
      const cellNode = Node.get(editor, cellNodePath);

      Transforms.setNodes(editor, {
        properties: {
          ...cellNode.properties,
          connected: false
        }
      }, {
        at: cellNodePath
      });

      Transforms.unwrapNodes(editor, {
        at: [...cellNodePath],
        match: n => n.type === 'link',
        split: true
      });

    }

  });

}

async function setupPagesManager(dataCollectionNode, pageId) {

  const manager = await serverInterface.startup.pages.setupPagesManagerForPage(pageId, {
    pageId,
    content: [HelptapEditor.paragraph.buildNode()],
    collectionId: dataCollectionNode.properties.collectionId,
    filterId: dataCollectionNode.properties.filterId,
    rowsCount: dataCollectionNode.properties.rowsCount,
  });

  return manager;

}

async function getRecordsForDataCollection(dataCollectionNode, pageId) {

  const records = await serverInterface.startup.pages.getRecordsForPageAndDataCollectionNode(pageId, dataCollectionNode);

  return records;

}

async function connectLink(editor, pageId, node, path) {

  const rowPath = Path.parent(path);
  const dataCollectionPath = Path.parent(rowPath);
  const dataCollectionNode = structuredClone(Node.get(editor, dataCollectionPath));

  const records = await getRecordsForDataCollection(dataCollectionNode, pageId);

  const templateNode = structuredClone(node);

  templateNode.properties.connected = true;

  dataCollectionNode.properties.template[path[path.length - 1]] = templateNode;

  const columns = dataCollectionNode.properties.columns;
  const template = dataCollectionNode.properties.template;

  let collectionRows = basicEditorValueSerializer.utilities.dataCollections.buildRows(columns, template, records);

  if (collectionRows.length > PREVIEW_ROWS_COUNT) {
    collectionRows.length = PREVIEW_ROWS_COUNT;
  }

  Editor.withoutNormalizing(editor, () => {

    HelptapEditor.setAttributes(editor, {
      properties: {
        ...dataCollectionNode.properties,
        connected: true,
        template,
        rows: records
      }
    }, dataCollectionPath);

    HelptapEditor.replaceChildren(editor, dataCollectionPath, collectionRows, dataCollectionNode.children.length);

  });

}

export async function processConnectLinkRequest(editor, pageId) {

  const path = HelptapEditor.getDraggableNodePath(editor);
  if (path === null) {
    return;
  }

  const node = Node.get(editor, path);
  const connected = isLinkNode(editor, node);

  if (connected === true) {
    disconnectLink(editor, node, path);
    return;
  }

  const rowPath = Path.parent(path);
  const dataCollectionPath = Path.parent(rowPath);
  const dataCollectionNode = Node.get(editor, dataCollectionPath);

  if (dataCollectionNode.properties.connected === true) {
    await connectLink(editor, pageId, node, path);
  } else {

    const page = await serverInterface.startup.pages.getPopulatedPageById(pageId, ['managerRoot']);

    if (page.managerRoot === true) {
      await connectLink(editor, pageId, node, path);
      return;
    }

    const confirmed = await application.prompts.confirm({
      header: 'Pages are not generated for this database',
      description: 'Do you want to generate them & connect?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No!'
    });

    if (confirmed === false) {
      return;
    }

    await connectLink(editor, pageId, node, path);
    await setupPagesManager(dataCollectionNode, pageId);

    application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
      message: 'Pages are being creaed! Please wait... They will be connected automatically',
      seconds: 5,
      type: 'info'
    });

  }

}